import React from "react";
import { Link } from "react-router-dom";
import "./Location.css";
import Icon from "../Icon";
import { faLanguage, faPhone, faShieldHeart, faUserDoctor, faXRay } from "@fortawesome/free-solid-svg-icons";
import SEO from "../SEO";
import Hours from "../Hours";
import { business, fullAddress, buildBreadcrumbJsonLd, DENTIST_ID, SITE_URL } from "../../seo/business";
import { services, locations, slugify } from "../../seo/routes";

const reasons = [
  {
    icon: faLanguage,
    title: "English, Russian and Ukrainian",
    text: "You can talk about your treatment in whichever language you're most comfortable with.",
  },
  {
    icon: faUserDoctor,
    title: "NYU-trained dentist",
    text: "Dr. Alina got her DDS at NYU College of Dentistry and did advanced general dentistry training at NYU Langone.",
  },
  {
    icon: faXRay,
    title: "Up-to-date equipment",
    text: "Digital X-rays, an intraoral scanner instead of messy impressions, and 3D CBCT imaging for implants.",
  },
  {
    icon: faShieldHeart,
    title: "Most PPO plans accepted",
    text: "Aetna, Cigna, Guardian, UnitedHealthcare, MetLife, EmblemHealth, Delta Dental, Anthem and more.",
  },
];

const Location = ({ location }) => {
  const path = `/${location.slug}`;
  const otherLocations = locations.filter((l) => l.slug !== location.slug);

  const jsonLd = [
    buildBreadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: location.seoTitle, path },
    ]),
    {
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      name: location.seoTitle,
      url: `${SITE_URL}${path}`,
      description: location.description,
      about: { "@id": DENTIST_ID },
      spatialCoverage: { "@type": "Place", name: `${location.name}, Brooklyn, NY` },
    },
  ];

  return (
    <main className="center-container fade-in" id="main-content">
      <SEO
        title={location.seoTitle}
        description={location.description}
        canonical={path}
        jsonLd={jsonLd}
      />
      <div className="location-container">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">{location.name}</span>
        </nav>

        <header className="location-header">
          <h1>{location.heading}</h1>
          <p>{location.intro}</p>
          <p>
            We do cleanings and checkups, fillings, crowns, root canals, gum
            treatment, extractions, implants and cosmetic work like whitening
            and veneers. We see patients 14 and up, and take most PPO insurance.
          </p>
          <a href={`tel:${business.phone}`} className="cta-button primary">
            <Icon icon={faPhone} /> Call {business.phone}
          </a>
        </header>

        <section className="location-section" aria-labelledby="why-heading">
          <h2 id="why-heading">Why Patients Choose Us</h2>
          <ul className="location-reasons">
            {reasons.map((reason) => (
              <li key={reason.title}>
                <Icon icon={reason.icon} />
                <div>
                  <h3>{reason.title}</h3>
                  <p>{reason.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section className="location-section" aria-labelledby="services-heading">
          <h2 id="services-heading">Our Services</h2>
          <ul className="location-services">
            {services.map((service) => (
              <li key={service.title}>
                <Link to={`/services/${slugify(service.title)}`}>
                  <h3>{service.title} Dentistry</h3>
                </Link>
                <p>{service.cards.map((card) => card.title).join(", ")}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="location-section location-visit" aria-labelledby="visit-heading">
          <div>
            <h2 id="visit-heading">Hours and Location</h2>
            <address>
              <strong>{business.name}</strong>
              <br />
              {fullAddress}
              <br />
              <a href={`tel:${business.phone}`}>{business.phone}</a>
            </address>
            <Hours />
          </div>
          <iframe
            title="Map of Benson Dental Studio"
            src={business.mapEmbedUrl}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </section>

        <nav className="location-section" aria-labelledby="areas-heading">
          <h2 id="areas-heading">
            {location.isHub ? "Neighborhoods We Serve" : "Also Serving"}
          </h2>
          <ul className="neighborhood-links">
            {otherLocations.map((l) => (
              <li key={l.slug}>
                <Link to={`/${l.slug}`}>{l.seoTitle}</Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </main>
  );
};

export default Location;
